import type {CornerDotType, CornerSquareType, DotType} from 'qr-code-styling';
import type {RGBA} from '@Utils/color.util';
import type {FillObject} from '@PosterWhiteboard/classes/fill.types';
import type {ImageItemSourceType} from '@Libraries/image-item.library';

/** A logo the user applied to a QR code, resolved through its own media source. */
export interface QRLogoReference {
  hashedFilename: string;
  fileExtension: string;
  source: ImageItemSourceType;
}

export const QR_MARGIN_MAX = 100;
export const QR_BACKGROUND_CORNER_RADIUS_MAX = 100;

export const QR_DEFAULT_FOREGROUND_COLOR: RGBA = [0, 0, 0, 1];
export const QR_DEFAULT_BACKGROUND_COLOR: RGBA = [255, 255, 255, 1];
export const QR_DEFAULT_MARGIN = 30;
export const QR_DEFAULT_BACKGROUND_CORNER_RADIUS = 12;
export const QR_DEFAULT_LOGO_SIZE_RATIO = 0.4;

export enum QRCodeDotType {
  SQUARE = 'square',
  ROUNDED = 'rounded',
  DOTS = 'dots',
  CLASSY = 'classy',
  CLASSY_ROUNDED = 'classy-rounded',
  EXTRA_ROUNDED = 'extra-rounded',
}

export enum QRCodeEyeFrameType {
  SQUARE = 'square',
  DOT = 'dot',
  EXTRA_ROUNDED = 'extra-rounded',
}

export enum QRCodeEyeBallType {
  SQUARE = 'square',
  DOT = 'dot',
}

export type QRCodeFileExtension = 'png' | 'svg';

export const enum QRType {
  LINK = 'qr_link',
  EMAIL = 'qr_email',
  PHONE_NUMBER = 'qr_phone_number',
}

export interface QRRenderConfig {
  data: string;
  sizePx: number;
  marginPercent: number;
  backgroundCornerRadiusPercent: number;
  dotStyle: QRCodeDotType;
  eyeFrameStyle: QRCodeEyeFrameType;
  eyeBallStyle: QRCodeEyeBallType;
  foregroundFill: FillObject;
  backgroundFill: FillObject;
  logoDataUrl: string | null;
  logoSizeRatio: number;
}

export const QR_DOT_TYPE_MAP: Record<QRCodeDotType, DotType> = {
  [QRCodeDotType.SQUARE]: 'square',
  [QRCodeDotType.ROUNDED]: 'rounded',
  [QRCodeDotType.DOTS]: 'dots',
  [QRCodeDotType.CLASSY]: 'classy',
  [QRCodeDotType.CLASSY_ROUNDED]: 'classy-rounded',
  [QRCodeDotType.EXTRA_ROUNDED]: 'extra-rounded',
};

export const QR_EYE_FRAME_TYPE_MAP: Record<QRCodeEyeFrameType, CornerSquareType> = {
  [QRCodeEyeFrameType.SQUARE]: 'square',
  [QRCodeEyeFrameType.DOT]: 'dot',
  [QRCodeEyeFrameType.EXTRA_ROUNDED]: 'extra-rounded',
};

export const QR_EYE_BALL_TYPE_MAP: Record<QRCodeEyeBallType, CornerDotType> = {
  [QRCodeEyeBallType.SQUARE]: 'square',
  [QRCodeEyeBallType.DOT]: 'dot',
};

export const QR_STYLE_PREVIEW_DIRECTORY = 'poster-editor-thumbnails/qr-style-previews/';
export const QR_STYLE_PREVIEW_EXTENSION = '.svg';

export enum QRCornerStyle {
  SQUARE = 'square',
  ROUNDED = 'rounded',
  ROUNDED_CIRCLE = 'rounded-circle',
  CIRCLE = 'circle',
}

export interface QRCornerStylePreset {
  id: QRCornerStyle;
  eyeFrameStyle: QRCodeEyeFrameType;
  eyeBallStyle: QRCodeEyeBallType;
}

export const QR_CORNER_STYLE_PRESETS: QRCornerStylePreset[] = [
  {id: QRCornerStyle.SQUARE, eyeFrameStyle: QRCodeEyeFrameType.SQUARE, eyeBallStyle: QRCodeEyeBallType.SQUARE},
  {id: QRCornerStyle.ROUNDED, eyeFrameStyle: QRCodeEyeFrameType.EXTRA_ROUNDED, eyeBallStyle: QRCodeEyeBallType.SQUARE},
  {id: QRCornerStyle.ROUNDED_CIRCLE, eyeFrameStyle: QRCodeEyeFrameType.EXTRA_ROUNDED, eyeBallStyle: QRCodeEyeBallType.DOT},
  {id: QRCornerStyle.CIRCLE, eyeFrameStyle: QRCodeEyeFrameType.DOT, eyeBallStyle: QRCodeEyeBallType.DOT},
];

export const getSelectedCornerStylePreset = (eyeFrameStyle: QRCodeEyeFrameType, eyeBallStyle: QRCodeEyeBallType): QRCornerStyle | undefined => {
  return QR_CORNER_STYLE_PRESETS.find((preset) => {
    return preset.eyeFrameStyle === eyeFrameStyle && preset.eyeBallStyle === eyeBallStyle;
  })?.id;
};

export const QR_PATTERN_PREVIEW_FILENAMES: Record<QRCodeDotType, string> = {
  [QRCodeDotType.SQUARE]: 'pattern-blocks',
  [QRCodeDotType.ROUNDED]: 'pattern-round-squares',
  [QRCodeDotType.DOTS]: 'pattern-circles',
  [QRCodeDotType.CLASSY]: 'pattern-classy',
  [QRCodeDotType.CLASSY_ROUNDED]: 'pattern-classy-rounded',
  [QRCodeDotType.EXTRA_ROUNDED]: 'pattern-extra-rounded',
};

export const QR_CORNER_PREVIEW_FILENAMES: Record<QRCornerStyle, string> = {
  [QRCornerStyle.SQUARE]: 'corner-1-square',
  [QRCornerStyle.ROUNDED]: 'corner-2-rounded',
  [QRCornerStyle.ROUNDED_CIRCLE]: 'corner-4-rounded-circle',
  [QRCornerStyle.CIRCLE]: 'corner-3-circle',
};

export const QR_PAYLOAD_MAX_BYTES = 1400;

export const QR_EVENT_DESCRIPTION_MAX_LENGTH = 80;

export enum QRPayloadType {
  URL = 'url',
  SOCIAL = 'social',
  TEXT = 'text',
  EMAIL = 'email',
  PHONE = 'phone',
  SMS = 'sms',
  WIFI = 'wifi',
  VCARD = 'vcard',
  CALENDAR_EVENT = 'calendar_event',
  PAYMENT_LINK = 'payment_link',
  APP_STORE = 'app_store',
  MULTIMEDIA = 'multimedia',
}

export enum QRWifiSecurity {
  WPA = 'wpa',
  WEP = 'wep',
  NONE = 'none',
}

export const QR_WIFI_SECURITY_TOKENS: Record<QRWifiSecurity, string> = {
  [QRWifiSecurity.WPA]: 'WPA',
  [QRWifiSecurity.WEP]: 'WEP',
  [QRWifiSecurity.NONE]: 'nopass',
};

export enum QRPayloadVariant {
  DEFAULT = 'default',
  APP_STORE = 'app_store',
  PLAY_STORE = 'play_store',
}

export enum QRPayloadIssueCode {
  REQUIRED = 'required',
  INVALID_URL = 'invalid_url',
  URL_HAS_WHITESPACE = 'url_has_whitespace',
  URL_UNSUPPORTED_SCHEME = 'url_unsupported_scheme',
  URL_HAS_CREDENTIALS = 'url_has_credentials',
  INVALID_EMAIL = 'invalid_email',
  PHONE_TOO_SHORT = 'phone_too_short',
  INVALID_DATE = 'invalid_date',
  DESCRIPTION_TOO_LONG = 'description_too_long',
  END_BEFORE_START = 'end_before_start',
  OVER_BYTE_CAP = 'over_byte_cap',
}

export const QR_ISSUE_BLOCKS_DOWNLOAD: Record<QRPayloadIssueCode, boolean> = {
  [QRPayloadIssueCode.REQUIRED]: true,
  [QRPayloadIssueCode.INVALID_URL]: true,
  [QRPayloadIssueCode.URL_HAS_WHITESPACE]: true,
  [QRPayloadIssueCode.URL_UNSUPPORTED_SCHEME]: true,
  [QRPayloadIssueCode.URL_HAS_CREDENTIALS]: true,
  [QRPayloadIssueCode.INVALID_EMAIL]: true,
  [QRPayloadIssueCode.PHONE_TOO_SHORT]: true,
  [QRPayloadIssueCode.INVALID_DATE]: true,
  [QRPayloadIssueCode.END_BEFORE_START]: true,
  [QRPayloadIssueCode.DESCRIPTION_TOO_LONG]: false,
  [QRPayloadIssueCode.OVER_BYTE_CAP]: true,
};

export enum QRPayloadField {
  LINK = 'link',
  TEXT_CONTENT = 'text_content',
  EMAIL_ADDRESS = 'email_address',
  PHONE_NUMBER = 'phone_number',
  SSID = 'ssid',
  PASSWORD = 'password',
  APP_STORE_LINK = 'app_store_link',
  PLAY_STORE_LINK = 'play_store_link',
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  TITLE = 'title',
  STARTS_AT = 'starts_at',
  ENDS_AT = 'ends_at',
  DESCRIPTION = 'description',
}

export interface QRPayloadIssue {
  code: QRPayloadIssueCode;
  field: QRPayloadField | null;
}

export interface QRCalendarDate {
  year: number;
  month: number;
  day: number;
}

export interface QRWallClock extends QRCalendarDate {
  hour: number;
  minute: number;
}

export interface QRPayloadCode {
  variant: QRPayloadVariant;
  payload: string;
  byteLength: number;
}

export interface QRPayloadResult {
  codes: [QRPayloadCode, ...QRPayloadCode[]];
  issues: QRPayloadIssue[];
}

export interface QRLinkInput {
  link: string;
}

export type QRUrlInput = QRLinkInput;

export interface QRSocialInput extends QRLinkInput {
  whatsAppMessage: string;
}

export interface QRTextInput {
  text: string;
}

export interface QREmailInput {
  emailAddress: string;
  subject: string;
  body: string;
}

export interface QRPhoneInput {
  phoneNumber: string;
}

export interface QRSmsInput {
  phoneNumber: string;
  message: string;
}

export interface QRWifiInput {
  ssid: string;
  security: QRWifiSecurity;
  password: string;
}

export interface QRVCardInput {
  firstName: string;
  lastName: string;
  organisation: string;
  jobTitle: string;
  mobileNumber: string;
  workNumber: string;
  faxNumber: string;
  emailAddress: string;
  website: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export interface QRCalendarEventInput {
  title: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  utcOffsetMinutes: number;
  location: string;
  description: string;
  generatedAt: Date;
}

export type QRPaymentLinkInput = QRLinkInput;

export interface QRAppStoreInput {
  appStoreLink: string;
  playStoreLink: string;
}

export type QRMultimediaInput = QRLinkInput;
